/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes } from '@adobe-commerce/elsie/lib';
import { useText } from '@adobe-commerce/elsie/i18n';
import { Button, InputDate, Modal } from '@adobe-commerce/elsie/components';
import '@/quote-management/components/ExpirationDateModal/ExpirationDateModal.css';

export interface ExpirationDateModalProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  open: boolean;
  expirationDate: string;
  expirationDateError?: string;
  errorBanner?: VNode;
  successBanner?: VNode;
  showCloseButton?: boolean;
  onExpirationDateChange: (value: string) => void;
  onSave: () => void | Promise<void> | Promise<boolean>;
  onClose?: () => void;
}

export const ExpirationDateModal: FunctionComponent<ExpirationDateModalProps> = ({
  className,
  open,
  expirationDate,
  expirationDateError,
  errorBanner,
  successBanner,
  showCloseButton = true,
  onExpirationDateChange,
  onSave,
  onClose,
}) => {
  const dictionary = useText({
    title: 'NegotiableQuoteTemplate.Manage.expirationDate.title',
    expirationDateLabel: 'NegotiableQuoteTemplate.Manage.expirationDate.expirationDateLabel',
    saveButton: 'NegotiableQuoteTemplate.Manage.expirationDate.saveButton',
    cancelButton: 'NegotiableQuoteTemplate.Manage.expirationDate.cancelButton',
  });

  if (!open) {
    return null;
  }

  return (
    <Modal
      open={open}
      size="medium"
      title={<>{dictionary.title}</>}
      onClose={onClose}
      clickToDismiss={true}
      escapeToDismiss={true}
      showCloseButton={showCloseButton}
      className={classes(['quote-management-expiration-date-modal', className])}
      data-testid="expiration-date-modal"
    >
      {errorBanner && (
        <div className={classes(['quote-management-expiration-date-modal__error-banner'])} data-testid="expiration-date-modal-error-banner">
          {errorBanner}
        </div>
      )}

      {successBanner && (
        <div className={classes(['quote-management-expiration-date-modal__success-banner'])} data-testid="expiration-date-modal-success-banner">
          {successBanner}
        </div>
      )}

      <div className={classes(['quote-management-expiration-date-modal__content'])}>
        <div>
          <InputDate
            name="expirationDate"
            value={expirationDate}
            onChange={(e: any) => onExpirationDateChange(e.target.value)}
            floatingLabel={dictionary.expirationDateLabel}
            required
            error={expirationDateError}
            data-testid="expiration-date-input"
          />
        </div>
      </div>

      <div className={classes(['quote-management-expiration-date-modal__actions'])}>
        <Button 
          variant="secondary"
          size="medium"
          onClick={onClose}
          className={classes(['quote-management-expiration-date-modal__cancel-button'])}
        >
          {dictionary.cancelButton}
        </Button>
        <Button 
          variant="primary"
          size="medium"
          onClick={onSave}
          className={classes(['quote-management-expiration-date-modal__save-button'])}
        >
          {dictionary.saveButton}
        </Button>
      </div>
    </Modal>
  );
};

